import { useCallback } from "react";
import { useAppDispatch } from "../useDispatch";
import { useApplySubscriptionVoucherMutation } from "../../api/voucher/voucherApiSlice";
import { setAppliedVoucher, setApplyingVoucher } from "../slice/subscription/subscriptionSlice";
import { enqueueNotification } from "../slice/notification/notificationSlice";
import { t } from "i18next";
import Logger from "../helpers/logger";
import ErrorTracker from "../helpers/errorTracker";
import { voucherRefusalMessage } from "../../util/voucherRefusal";

export const useApplySubscriptionVoucher = () => {
    const [applyVoucherMutation] = useApplySubscriptionVoucherMutation();
    const dispatch = useAppDispatch();

    return useCallback(
        async (commercialModelId: string, voucherCode: string) => {
            dispatch(setApplyingVoucher(true));

            try {
                const response = await applyVoucherMutation({
                    commercialModelId,
                    code: voucherCode,
                }).unwrap();

                if (response.success && response.data) {
                    // Store voucher data in subscription Redux state
                    dispatch(
                        setAppliedVoucher({
                            voucherId: response.data.voucherId,
                            code: voucherCode,
                            originalAmount: response.data.originalAmount,
                            newAmount: response.data.newAmount,
                            discount: response.data.discount,
                        })
                    );

                    dispatch(setApplyingVoucher(false));

                    dispatch(
                        enqueueNotification({
                            severity: "success",
                            message: t("book_location.success.coupon_applied"),
                        })
                    );

                    return response.data;
                }
            } catch (error) {
                dispatch(setApplyingVoucher(false));
                // #3052: say why a found code was refused (bound to a station,
                // a prepaid voucher, expired, at its limit, ...); otherwise "invalid".
                const refusal = voucherRefusalMessage(error);
                dispatch(
                    enqueueNotification({
                        severity: "error",
                        message: t(refusal.key, refusal.params),
                    })
                );

                Logger.for("applySubscriptionVoucher").error(error);
                ErrorTracker.trackException(error as Error);
            }
        },
        [applyVoucherMutation, dispatch]
    );
};
